// src/components/RelativeStrength.jsx
// 수급 탭 — 여러 종목의 기간별 상대강도. 기간 시작일을 100으로 리베이스한 주가 곡선 비교.
// 데이터는 /api/history(야후 adjClose)를 2년치 한 번만 받아두고 기간 전환은 클라이언트 슬라이스.
import React, { useState, useEffect, useMemo } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid,
} from "recharts";
import { searchStocks } from "../lib/krStocks";

const PERIODS = [
  ["1M", 1], ["3M", 3], ["6M", 6], ["9M", 9], ["1Y", 12], ["YTD", "ytd"],
];
const COLORS = ["#1a5f7a", "#e84040", "#0a7c59", "#b8860b", "#7b52ab", "#e67e22", "#1f5fd0", "#c2185b"];
const MAX_STOCKS = 8;

function cutoffTs(period) {
  const d = new Date();
  if (period === "ytd") return Math.floor(new Date(d.getFullYear(), 0, 1).getTime() / 1000);
  d.setMonth(d.getMonth() - period);
  return Math.floor(d.getTime() / 1000);
}
const fmtDate = ts => {
  const d = new Date(ts * 1000);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
};

function AddSearch({ onPick, disabled }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const matches = open && q ? searchStocks(q, 8) : [];
  return (
    <div style={{ position: "relative", width: "min(280px, 60vw)" }}>
      <input value={q} disabled={disabled}
        placeholder={disabled ? `최대 ${MAX_STOCKS}종목` : "비교할 종목 추가"}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, boxSizing: "border-box" }} />
      {matches.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, marginTop: 3, maxHeight: 240, overflowY: "auto", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }}>
          {matches.map(m => (
            <div key={m.c} onMouseDown={() => { onPick(m); setQ(""); setOpen(false); }}
              style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = "#f2f5fb"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <b>{m.n}</b> <span style={{ color: "#aaa" }}>{m.c}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RelativeStrength() {
  const [stocks, setStocks] = useState([]);       // [{n,c,m}]
  const [series, setSeries] = useState({});       // ticker -> {points:[[ts,close]]}
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [period, setPeriod] = useState(3);

  const symKey = stocks.map(s => `${s.c}:${s.m}`).join(",");

  useEffect(() => {
    if (!symKey) { setSeries({}); return; }
    let cancelled = false;
    setLoading(true); setErr("");
    fetch(`/api/history?symbols=${encodeURIComponent(symKey)}&range=2y&interval=1d`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.error) setErr(d.error);
        else setSeries(d.series || {});
      })
      .catch(e => { if (!cancelled) setErr(String(e.message || e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symKey]);

  // 기간 슬라이스 → 각 종목 첫날=100 리베이스 → 공통 날짜축으로 병합
  const { chart, ranks } = useMemo(() => {
    const from = cutoffTs(period);
    const rebased = [];   // {c, n, color, map:Map(ts→idx), ret}
    stocks.forEach((s, i) => {
      const pts = (series[s.c]?.points || []).filter(([ts, p]) => ts >= from && p > 0);
      if (pts.length < 2) return;
      const base = pts[0][1];
      const map = new Map(pts.map(([ts, p]) => [ts, (p / base) * 100]));
      rebased.push({
        c: s.c, n: s.n, color: COLORS[i % COLORS.length], map,
        ret: (pts[pts.length - 1][1] / base - 1) * 100,
      });
    });
    if (!rebased.length) return { chart: [], ranks: [] };

    const allTs = [...new Set(rebased.flatMap(r => [...r.map.keys()]))].sort((a, b) => a - b);
    const last = {};
    const chart = allTs.map(ts => {
      const row = { ts };
      rebased.forEach(r => {
        if (r.map.has(ts)) last[r.c] = r.map.get(ts);
        if (last[r.c] != null) row[r.c] = last[r.c];   // 휴장 갭은 직전값 유지
      });
      return row;
    });
    return { chart, ranks: [...rebased].sort((a, b) => b.ret - a.ret) };
  }, [stocks, series, period]);

  const domain = useMemo(() => {
    const vals = chart.flatMap(row => Object.entries(row).filter(([k]) => k !== "ts").map(([, v]) => v));
    if (!vals.length) return [90, 110];
    const lo = Math.min(...vals), hi = Math.max(...vals), pad = Math.max((hi - lo) * 0.08, 1);
    return [Math.floor(lo - pad), Math.ceil(hi + pad)];
  }, [chart]);

  const addStock = m => setStocks(p => (p.some(s => s.c === m.c) || p.length >= MAX_STOCKS ? p : [...p, m]));
  const removeStock = c => setStocks(p => p.filter(s => s.c !== c));

  return (
    <div style={{ marginTop: 28, paddingTop: 24, borderTop: "1px solid #eee" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>📈 상대강도 비교</h2>
        <AddSearch onPick={addStock} disabled={stocks.length >= MAX_STOCKS} />
        <div style={{ display: "flex", border: "1px solid #e2e2e2", borderRadius: 8, overflow: "hidden" }}>
          {PERIODS.map(([lab, v]) => (
            <button key={lab} onClick={() => setPeriod(v)} style={{
              padding: "6px 11px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
              background: period === v ? "#1a1a2e" : "#fff", color: period === v ? "#fff" : "#888",
            }}>{lab}</button>
          ))}
        </div>
      </div>

      {stocks.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {stocks.map((s, i) => {
            const r = ranks.find(x => x.c === s.c);
            return (
              <div key={s.c} style={{
                display: "flex", alignItems: "center", gap: 7, padding: "5px 10px", borderRadius: 8,
                background: "#fff", border: "1px solid #e8e8e8", fontSize: 12,
              }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
                <b>{s.n}</b>
                {r && <span style={{ fontWeight: 700, color: r.ret >= 0 ? "#c0392b" : "#1f5fd0" }}>
                  {r.ret >= 0 ? "+" : ""}{r.ret.toFixed(1)}%
                </span>}
                <button onClick={() => removeStock(s.c)} style={{ background: "none", border: "none", cursor: "pointer", color: "#bbb", fontSize: 13, padding: 0, lineHeight: 1 }}>✕</button>
              </div>
            );
          })}
        </div>
      )}

      {!stocks.length ? (
        <div style={{ padding: 46, textAlign: "center", color: "#aaa", background: "#fafafa", borderRadius: 12, border: "1px dashed #ddd" }}>
          종목을 추가하면 기간 시작일=100 기준으로 <b>상대강도 곡선</b>을 비교합니다.
        </div>
      ) : loading && !chart.length ? (
        <div style={{ padding: 46, textAlign: "center", color: "#aaa" }}>주가 불러오는 중…</div>
      ) : err ? (
        <div style={{ padding: 40, textAlign: "center", color: "#c0392b" }}>⚠️ {err}</div>
      ) : !chart.length ? (
        <div style={{ padding: 40, textAlign: "center", color: "#aaa" }}>해당 기간 데이터가 부족합니다.</div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 14 }}>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chart} margin={{ top: 8, right: 18, bottom: 4, left: 0 }}>
              <CartesianGrid stroke="#f4f4f4" vertical={false} />
              <XAxis dataKey="ts" tickFormatter={fmtDate} tick={{ fontSize: 10, fill: "#999" }} minTickGap={40} />
              <YAxis domain={domain} tick={{ fontSize: 10, fill: "#999" }} width={44}
                tickFormatter={v => v.toFixed(0)} />
              <ReferenceLine y={100} stroke="#ccc" strokeDasharray="3 3" />
              <Tooltip
                labelFormatter={ts => new Date(ts * 1000).toLocaleDateString("ko-KR")}
                formatter={(v, code) => {
                  const r = ranks.find(x => x.c === code);
                  return [`${v.toFixed(1)} (${v >= 100 ? "+" : ""}${(v - 100).toFixed(1)}%)`, r ? r.n : code];
                }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e8e8e8" }}
                itemSorter={item => -item.value}
              />
              {stocks.map((s, i) => (
                <Line key={s.c} type="monotone" dataKey={s.c} stroke={COLORS[i % COLORS.length]}
                  strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 10, color: "#bbb", textAlign: "right", marginTop: 4 }}>
            기간 첫 거래일 = 100 (배당 반영 수정주가) · 야후 파이낸스
          </div>
        </div>
      )}
    </div>
  );
}
